// Stale-tab chunk self-heal — the single source of truth shared by the
// router's lazy imports, the global `vite:preloadError` listener, and
// the top-level error boundary.
//
// After a deploy the hashed chunk filenames change and the OLD chunks
// are deleted from the server. A tab still running the previous build
// then requests a now-404 chunk — a route-level lazy import, a modal
// chunk pulled on demand (Campaign Studio's NameTemplateModal /
// CsvUploadPopup, the Rules "Apply To" popup), a vendor split, a
// modulepreload. Reload ONCE to pull the fresh index.html (served
// no-store) + current chunk hashes.
//
// The guard is TIME-BASED, never a sticky boolean. A sticky "reloaded"
// flag cleared only on success gets stuck forever once a reload lands
// on a different route (or a second deploy races the reload) — the next
// stale chunk then surfaces the error with no reload, which is exactly
// the "periodically errors, refresh fixes it" report (CLAUDE.md §56.3).
// A self-expiring timestamp can't get stuck: we suppress a re-reload
// only if we *just* reloaded (< _SUPPRESS_MS ago, i.e. the reload
// itself STILL failed → genuine breakage / mid-deploy → surface it).
// Any later deploy is well past the window, so healing works again.

const _RELOAD_AT_KEY = "oneads.chunk_reload_at";
const _SUPPRESS_MS = 15_000;

// Set the moment THIS page issued a reload; lives in module scope, so a
// fresh page load starts at false. Two self-heal paths fire within the
// same second on a stale tab: Vite's preload of the chunk's dependencies
// (`vite:preloadError`) and the chunk's own `import()` (lazyWithReload).
// The time guard rightly refuses the SECOND reload — but "refused because
// one is already on its way" must not become "throw the error": React
// Router's route boundary would paint "Unexpected Application Error!" for
// the 1–3 s until the pending reload lands (CLAUDE.md §117).
let _issuedThisPage = false;

/** A reload has been issued by this page and has not landed yet — hold
 *  (never resolve, render the quiet placeholder) instead of throwing. */
export function reloadPending(): boolean {
  return _issuedThisPage;
}

/** Reload once to heal a stale chunk. Returns true if the reload was
 *  issued, false if suppressed (we reloaded moments ago and it still
 *  failed → let the caller surface the error instead of looping). */
export function reloadForStaleChunk(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(_RELOAD_AT_KEY)) || 0;
  } catch {
    // sessionStorage can throw in private-mode / sandboxed frames.
  }
  if (Date.now() - last < _SUPPRESS_MS) return false;
  try {
    sessionStorage.setItem(_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  _issuedThisPage = true;
  window.location.reload();
  return true;
}

/** True for the "a dynamically-imported chunk failed to load" error
 *  class across browsers (Chrome / Firefox / Safari / legacy Vite).
 *  Lets the error boundary tell a stale-tab chunk 404 (self-heal via a
 *  silent reload) apart from a genuine app crash (show "Something
 *  broke"). */
export function isChunkLoadError(err: unknown): boolean {
  const name = (err as { name?: string })?.name ?? "";
  if (name === "ChunkLoadError") return true;
  const msg = String(
    (err as { message?: string })?.message ?? err ?? "",
  ).toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("importing a module script") ||
    msg.includes("module script failed") ||
    msg.includes("failed to fetch dynamically") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    // Vite's own message when a chunk's stylesheet 404s after a deploy.
    msg.includes("unable to preload css")
  );
}
